/**
 * Settings Migration Service
 * 
 * Migrates existing user data from localStorage and old Dexie stores
 * to the new unified settings system. Runs once per user.
 */

import { settingsStore } from './settingsStore';
import type { OnboardingSetting } from './settingsStore';

// ========================================
// Migration Constants
// ========================================

const MIGRATION_KEY = 'macrotracker:settings:migration';
const MIGRATION_VERSION = 1;

// Old localStorage keys
const OLD_FEATURE_FLAG_PREFIX = 'macrotracker:feature:';
const OLD_ONBOARDING_KEY = 'macrotracker:onboarding:completed';
const OLD_ONBOARDING_COMPLETED_AT_KEY = 'macrotracker:onboarding:completedAt';

// Migration result type
export interface MigrationResult {
  success: boolean;
  migratedItems: string[];
  errors: string[];
  alreadyMigrated: boolean;
}

// ========================================
// Main Migration Functions
// ========================================

/**
 * Checks if settings migration has already been performed
 */
export function isSettingsMigrationComplete(): boolean {
  try {
    const migrationData = localStorage.getItem(MIGRATION_KEY);
    if (!migrationData) return false;
    
    const parsed = JSON.parse(migrationData);
    return parsed.version === MIGRATION_VERSION && parsed.completed === true;
  } catch {
    return false;
  }
}

/**
 * Marks settings migration as complete
 */
function markMigrationComplete(): void {
  try {
    localStorage.setItem(MIGRATION_KEY, JSON.stringify({
      version: MIGRATION_VERSION,
      completed: true,
      completedAt: new Date().toISOString(),
    }));
  } catch (error) {
    console.error('Failed to mark migration as complete:', error);
  }
}

/**
 * Runs the complete settings migration
 * Migrates data from localStorage and old storage to unified settings
 */
export async function runSettingsMigration(): Promise<MigrationResult> {
  const result: MigrationResult = {
    success: true,
    migratedItems: [],
    errors: [],
    alreadyMigrated: false,
  };

  try {
    // Check if already migrated
    if (isSettingsMigrationComplete()) {
      result.alreadyMigrated = true;
      return result;
    }

    // Migrate feature flags from localStorage
    const featureFlagResult = await migrateFeatureFlags();
    result.migratedItems.push(...featureFlagResult.migratedItems);
    result.errors.push(...featureFlagResult.errors);

    // Migrate onboarding status from localStorage
    const onboardingResult = await migrateOnboardingStatus();
    result.migratedItems.push(...onboardingResult.migratedItems);
    result.errors.push(...onboardingResult.errors);

    // Mark migration as complete
    if (result.errors.length === 0) {
      markMigrationComplete();
      result.success = true;
    } else {
      result.success = false;
    }

    // Log migration results
    if (result.migratedItems.length > 0) {
      console.log('Settings migration completed:', result.migratedItems);
    }
    if (result.errors.length > 0) {
      console.error('Settings migration errors:', result.errors);
    }

    return result;
  } catch (error) {
    result.success = false;
    result.errors.push(`Migration failed: ${error instanceof Error ? error.message : String(error)}`);
    return result;
  }
}

// ========================================
// Feature Flags Migration
// ========================================

interface FeatureFlagMigrationResult {
  migratedItems: string[];
  errors: string[];
}

/**
 * Migrates feature flags from localStorage to unified settings
 */
async function migrateFeatureFlags(): Promise<FeatureFlagMigrationResult> {
  const result: FeatureFlagMigrationResult = {
    migratedItems: [],
    errors: [],
  };

  try {
    // Find all feature flag keys in localStorage
    const featureFlagKeys: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(OLD_FEATURE_FLAG_PREFIX)) {
        featureFlagKeys.push(key);
      }
    }

    // Migrate each feature flag
    for (const key of featureFlagKeys) {
      try {
        const value = localStorage.getItem(key);
        if (value !== null) {
          // Extract flag name from key (remove prefix)
          const flagName = key.replace(OLD_FEATURE_FLAG_PREFIX, '');
          
          // Parse the value (stored as JSON)
          let parsedValue: boolean;
          try {
            parsedValue = JSON.parse(value);
            if (typeof parsedValue !== 'boolean') {
              // If not a boolean, try to interpret it
              parsedValue = Boolean(parsedValue);
            }
          } catch {
            // If JSON parse fails, treat as string
            parsedValue = value === 'true';
          }

          // Save to unified settings
          await settingsStore.save('featureFlag', flagName, parsedValue);
          result.migratedItems.push(`featureFlag:${flagName}`);
          
          // Remove old localStorage entry
          localStorage.removeItem(key);
        }
      } catch (error) {
        result.errors.push(`Failed to migrate feature flag ${key}: ${error instanceof Error ? error.message : String(error)}`);
      }
    }
  } catch (error) {
    result.errors.push(`Feature flag migration failed: ${error instanceof Error ? error.message : String(error)}`);
  }

  return result;
}

// ========================================
// Onboarding Status Migration
// ========================================

interface OnboardingMigrationResult {
  migratedItems: string[];
  errors: string[];
}

/**
 * Migrates onboarding status from localStorage to unified settings
 */
async function migrateOnboardingStatus(): Promise<OnboardingMigrationResult> {
  const result: OnboardingMigrationResult = {
    migratedItems: [],
    errors: [],
  };

  try {
    const completed = localStorage.getItem(OLD_ONBOARDING_KEY);
    const completedAt = localStorage.getItem(OLD_ONBOARDING_COMPLETED_AT_KEY);

    if (completed !== null) {
      // Parse the value
      let isCompleted: boolean;
      try {
        isCompleted = JSON.parse(completed);
      } catch {
        isCompleted = completed === 'true';
      }

      if (isCompleted) {
        // Build onboarding setting
        const onboardingSetting: OnboardingSetting = {
          completed: true,
        };

        // Add completedAt if available
        if (completedAt) {
          try {
            // Validate it's a valid date
            const date = new Date(completedAt);
            if (!isNaN(date.getTime())) {
              onboardingSetting.completedAt = completedAt;
            }
          } catch {
            // Invalid date, ignore
          }
        }

        // Save to unified settings
        await settingsStore.save('onboarding', 'completed', onboardingSetting);
        result.migratedItems.push('onboarding:completed');
      } else {
        // Onboarding not completed, just set to false
        await settingsStore.save('onboarding', 'completed', { completed: false });
        result.migratedItems.push('onboarding:completed');
      }

      // Clean up old localStorage entries
      localStorage.removeItem(OLD_ONBOARDING_KEY);
      localStorage.removeItem(OLD_ONBOARDING_COMPLETED_AT_KEY);
    }
  } catch (error) {
    result.errors.push(`Onboarding migration failed: ${error instanceof Error ? error.message : String(error)}`);
  }

  return result;
}

// ========================================
// Migration Reset (for testing/debugging)
// ========================================

/**
 * Resets the migration status, allowing migration to run again
 * Useful for testing or if migration needs to be re-run
 */
export function resetMigrationStatus(): void {
  try {
    localStorage.removeItem(MIGRATION_KEY);
  } catch (error) {
    console.error('Failed to reset migration status:', error);
  }
}

/**
 * Gets the current migration status
 */
export function getMigrationStatus(): {
  completed: boolean;
  version: number | null;
  completedAt: string | null;
} {
  try {
    const migrationData = localStorage.getItem(MIGRATION_KEY);
    if (!migrationData) {
      return { completed: false, version: null, completedAt: null };
    }

    const parsed = JSON.parse(migrationData);
    return {
      completed: parsed.completed === true,
      version: parsed.version || null,
      completedAt: parsed.completedAt || null,
    };
  } catch {
    return { completed: false, version: null, completedAt: null };
  }
}

// ========================================
// Service API
// ========================================

export const settingsMigration = {
  run: runSettingsMigration,
  isComplete: isSettingsMigrationComplete,
  reset: resetMigrationStatus,
  getStatus: getMigrationStatus,
};

export default settingsMigration;
